/**
 * Read a response body line by line without ever holding it as one string.
 * A long recording is more NDJSON than the browser's longest string (a
 * 2 h 14 min staging recording is 1.1 GB once inflated), so `resp.text()`
 * fails on it. Lines that straddle chunks, and multi-byte characters that
 * straddle them, are carried over.
 *
 * `onProgress` gets the bytes read so far (decompressed: the browser
 * inflates a gzip-encoded recording before the body reaches us), at most
 * every `PROGRESS_STEP` bytes. Returns false when `isCancelled` stopped it.
 */
export const PROGRESS_STEP = 8_000_000;

export async function readLines(
  resp: Response,
  onLine: (line: string) => void,
  onProgress?: (bytes: number) => void,
  isCancelled: () => boolean = () => false,
): Promise<boolean> {
  if (!resp.body || typeof resp.body.getReader !== 'function') {
    const text = await resp.text();
    for (const line of text.split('\n')) onLine(line);
    return true;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let carry = '';
  let bytes = 0;
  let reported = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (isCancelled()) {
      void reader.cancel();
      return false;
    }
    if (done) break;
    bytes += value.byteLength;
    if (bytes - reported >= PROGRESS_STEP) {
      reported = bytes;
      onProgress?.(bytes);
      // Buffered chunks resolve as microtasks; a real yield lets a page
      // (or a worker's message queue) breathe.
      await new Promise((r) => setTimeout(r, 0));
    }
    const lines = (carry + decoder.decode(value, { stream: true })).split('\n');
    carry = lines.pop() ?? '';
    for (const line of lines) onLine(line);
  }
  onLine(carry + decoder.decode());
  return true;
}
